// Shared model + geometry for the multi-point crossing PATHS drawn in the
// Configure "Lines" tab and evaluated in Execute.
//
// A path = { id, name, points: [{x,y}...] } in normalized (0-1) video space.
// Consecutive points form ordered "gates" (segments). A person/object crosses
// the path FORWARD when it passes gate 0, then 1, ... in increasing order;
// BACKWARD is the reverse. Bezier smoothing is display-only; crossing math uses
// the straight control segments.

const KEY = 'aiCodePaths'

export function loadPaths() {
    try {
        const v = JSON.parse(sessionStorage.getItem(KEY))
        return Array.isArray(v) ? v : []
    } catch {
        return []
    }
}

export function savePaths(paths) {
    sessionStorage.setItem(KEY, JSON.stringify(paths))
}

// Catmull-Rom -> smooth sampled polyline through the control points, for a
// nice curved look. Returns [{x,y}] (normalized). <3 points => the points.
export function bezierPoints(points, perSeg = 16) {
    if (!points || points.length < 3) return points || []
    const pts = points
    const out = []
    const pt = (i) => pts[Math.max(0, Math.min(pts.length - 1, i))]
    for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pt(i - 1)
        const p1 = pt(i)
        const p2 = pt(i + 1)
        const p3 = pt(i + 2)
        for (let s = 0; s < perSeg; s++) {
            const t = s / perSeg
            const t2 = t * t
            const t3 = t2 * t
            out.push({
                x:
                    0.5 *
                    (2 * p1.x +
                        (-p0.x + p2.x) * t +
                        (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 +
                        (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3),
                y:
                    0.5 *
                    (2 * p1.y +
                        (-p0.y + p2.y) * t +
                        (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 +
                        (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3),
            })
        }
    }
    out.push(pts[pts.length - 1])
    return out
}

const ccw = (a, b, c) => (c.y - a.y) * (b.x - a.x) - (b.y - a.y) * (c.x - a.x)
export function segmentsIntersect(a, b, c, d) {
    const d1 = ccw(c, d, a)
    const d2 = ccw(c, d, b)
    const d3 = ccw(a, b, c)
    const d4 = ccw(a, b, d)
    return (
        ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
        ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))
    )
}

// Per-target crossing state for one path. Keep a mutable `state` per
// (path, target-id) — it just holds the last crossing time for a cooldown.
// Feed prev/cur centre each frame; returns 'forward' | 'backward' | null.
//
// Direction is the SIDE the target crosses a segment: using the drawn segment's
// orientation (start -> end defines "forward"), the sign of (segment × motion)
// tells which way it went. This is correct for a simple 2-point line and for
// any gate of a multi-point path, and — unlike an ordered-gate sweep — it fires
// predictably the moment the target crosses.
export function makePathState() {
    return { at: 0 }
}

export function stepPathCrossing(path, state, prev, cur, now = 0, cooldown = 700) {
    const pts = path.points || []
    if (pts.length < 2 || !prev) return null
    for (let g = 0; g < pts.length - 1; g++) {
        if (!segmentsIntersect(prev, cur, pts[g], pts[g + 1])) continue
        if (now - (state.at || 0) < cooldown) return null
        state.at = now
        const gx = pts[g + 1].x - pts[g].x
        const gy = pts[g + 1].y - pts[g].y
        const mx = cur.x - prev.x
        const my = cur.y - prev.y
        const cross = gx * my - gy * mx
        return cross >= 0 ? 'forward' : 'backward'
    }
    return null
}

// Tolerance (normalized 0-1) added around the object's box when testing whether
// it "touches" a path point. Raise it to make touching a point more forgiving,
// lower it to require the box to land more precisely on the point.
export const PATH_TOUCH_MARGIN = 0.04

// Ordered "travel the whole path" detection for a single target. Unlike
// stepPathCrossing (which fires the instant the target cuts ANY segment), this
// requires the target to touch EVERY point of the path IN ORDER, and it tracks
// BOTH directions at once: start -> end (returns 'forward') and end -> start
// (returns 'backward'). A point is "touched" when the target's bounding box —
// any edge/corner, expanded by `radius` — covers it (falls back to the centre
// when no box is given). Completing a direction returns once, then re-arms after
// `cooldown`; partial progress is dropped if the target stalls for `resetMs`.
export function makePathProgress() {
    return { fwd: 0, bwd: 0, fwdAt: 0, bwdAt: 0, doneAt: 0 }
}

export function stepPathProgress(path, state, target, now = 0, opts = {}) {
    const pts = path.points || []
    if (pts.length < 2 || !target) return null
    const margin = opts.radius != null ? opts.radius : PATH_TOUCH_MARGIN
    const resetMs = opts.resetMs != null ? opts.resetMs : 4000
    const cooldown = opts.cooldown != null ? opts.cooldown : 1500

    const box = target.box
    const touches = (p) => {
        if (!p) return false
        if (box) {
            return (
                p.x >= box.x - margin &&
                p.x <= box.x + box.w + margin &&
                p.y >= box.y - margin &&
                p.y <= box.y + box.h + margin
            )
        }
        return Math.hypot((target.x || 0) - p.x, (target.y || 0) - p.y) <= margin
    }

    // Guard against a shrunk path; drop stale partial progress on a stall.
    if (state.fwd > pts.length) state.fwd = 0
    if (state.bwd > pts.length) state.bwd = 0
    if (state.fwd > 0 && now - state.fwdAt > resetMs) state.fwd = 0
    if (state.bwd > 0 && now - state.bwdAt > resetMs) state.bwd = 0

    let result = null

    // Forward: point 0 -> last.
    if (touches(pts[state.fwd])) {
        state.fwd += 1
        state.fwdAt = now
        if (state.fwd >= pts.length) {
            state.fwd = 0
            if (now - (state.doneAt || 0) > cooldown) {
                state.doneAt = now
                result = 'forward'
            }
        }
    }
    // Backward: last point -> 0.
    if (touches(pts[pts.length - 1 - state.bwd])) {
        state.bwd += 1
        state.bwdAt = now
        if (state.bwd >= pts.length) {
            state.bwd = 0
            if (now - (state.doneAt || 0) > cooldown) {
                state.doneAt = now
                result = result || 'backward'
            }
        }
    }
    return result
}
